import React, { useEffect, useMemo, useState } from 'react';
import styles from './AdminDashboard.module.css';
import {
  fetchAdminStats,
  fetchHomeUsers,
  fetchBusinessUsers,
  fetchLatestJobs,
  fetchLatestInteractions,
  fetchSubscriptionTypes,
  fetchQuoteTemplates
} from '../../services/adminService';

const StatCard = ({ title, value, note }) => (
  <div className={styles.card}>
    <div className={styles.cardTitle}>{title}</div>
    <div className={styles.cardValue}>{Number.isFinite(value) ? value : (value || 0)}</div>
    {note ? <div className={styles.cardNote}>{note}</div> : null}
  </div>
);

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const [homeUsers, setHomeUsers] = useState([]);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [quoteTemplates, setQuoteTemplates] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const s = await fetchAdminStats();
      setStats(s);

      // Datasets for tables
      const [hu, bu, j, itx, subs, qt] = await Promise.all([
        fetchHomeUsers(),
        fetchBusinessUsers(),
        fetchLatestJobs(),
        fetchLatestInteractions(),
        fetchSubscriptionTypes(),
        fetchQuoteTemplates()
      ]);

      setHomeUsers(hu || []);
      setBusinessUsers(bu || []);
      setJobs(j || []);
      setInteractions(itx || []);
      setSubscriptionTypes(subs || []);
      setQuoteTemplates(qt || []);
    } catch (err) {
      console.error('AdminDashboard loadAll failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topHomeUsers = useMemo(() => (homeUsers || []).slice(0, 10), [homeUsers]);
  const topBusinessUsers = useMemo(() => (businessUsers || []).slice(0, 10), [businessUsers]);
  const topJobs = useMemo(() => (jobs || []).slice(0, 10), [jobs]);
  const topInteractions = useMemo(() => (interactions || []).slice(0, 10), [interactions]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>Admin Dashboard</div>
        <div className={styles.actions}>
          <button className={styles.button} onClick={loadAll}>Refresh</button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className={styles.grid}>
        {loading ? (
          <>
            <div className={styles.card}><div className={styles.skeleton} /></div>
            <div className={styles.card}><div className={styles.skeleton} /></div>
            <div className={styles.card}><div className={styles.skeleton} /></div>
            <div className={styles.card}><div className={styles.skeleton} /></div>
            <div className={styles.card}><div className={styles.skeleton} /></div>
          </>
        ) : (
          <>
            <StatCard title="Home Users" value={stats?.counts?.homeUsers} />
            <StatCard title="Business Users" value={stats?.counts?.businessUsers} />
            <StatCard
              title="Active Subs"
              value={stats?.counts?.subscriptionsActive ?? stats?.counts?.subscriptionTypes}
              note={stats?.counts?.subscriptionsActive != null ? "Active subscriptions" : "Catalog of plans"}
            />
            <StatCard
              title={stats?.counts?.jobsTotal != null ? "Jobs (Total)" : "Latest Jobs"}
              value={stats?.counts?.jobsTotal ?? stats?.counts?.jobsLatest}
              note={stats?.notes?.jobs}
            />
            <StatCard
              title={stats?.counts?.quotesTotal != null ? "Quotes (Total)" : "Quote Templates"}
              value={stats?.counts?.quotesTotal ?? stats?.counts?.quoteTemplates}
              note={stats?.notes?.quotes}
            />
          </>
        )}
      </div>

      {/* Secondary KPIs */}
      <div className={styles.kpiGrid3}>
        {loading ? (
          <>
            <div className={styles.card}><div className={styles.skeleton} /></div>
            <div className={styles.card}><div className={styles.skeleton} /></div>
            <div className={styles.card}><div className={styles.skeleton} /></div>
          </>
        ) : (
          <>
            <StatCard title="Interactions" value={stats?.counts?.interactions ?? stats?.counts?.activities} note="Latest social activity" />
            <StatCard
              title="Messages"
              value={stats?.counts?.messages ?? "—"}
              note={stats?.counts?.messages != null ? "Total messages across system" : "Add admin endpoint"}
            />
            <StatCard
              title="Revenue (30d)"
              value={typeof stats?.counts?.revenueLast30Days !== "undefined" ? `$${stats?.counts?.revenueLast30Days}` : "—"}
              note={stats?.counts?.revenueLast30Days != null ? "USD last 30 days" : "Add admin revenue endpoint"}
            />
          </>
        )}
      </div>

      {/* Data Sections */}
      <div className={styles.sections} style={{ marginTop: 24 }}>
        <div className={styles.panel}>
          <div className={styles.panelTitle}>Users (Home)</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User Id</th>
                  <th>Email</th>
                  <th>First</th>
                  <th>Last</th>
                  <th>Suburb</th>
                  <th>Postcode</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={6}><div className={styles.skeleton} /></td></tr>
                  ))
                ) : (
                  topHomeUsers.map(u => (
                    <tr key={u.user_id || u.userId || u.email}>
                      <td>{u.user_id || u.userId}</td>
                      <td>{u.email}</td>
                      <td>{u.firstName}</td>
                      <td>{u.lastName}</td>
                      <td>{u.suburb}</td>
                      <td>{u.postcode}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelTitle}>Users (Business)</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Business User Id</th>
                  <th>Business Id</th>
                  <th>Business Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Suburb</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={6}><div className={styles.skeleton} /></td></tr>
                  ))
                ) : (
                  topBusinessUsers.map(b => (
                    <tr key={b.businessUserId || b.email}>
                      <td>{b.businessUserId}</td>
                      <td>{b.businessId}</td>
                      <td>{b.businessName}</td>
                      <td>{b.email}</td>
                      <td>{b.phone}</td>
                      <td>{b.suburb}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={styles.sections} style={{ marginTop: 16 }}>
        <div className={styles.panel}>
          <div className={styles.panelTitle}>Jobs (Latest)</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Job Id</th>
                  <th>Name</th>
                  <th>Postcode</th>
                  <th>Created</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={5}><div className={styles.skeleton} /></td></tr>
                  ))
                ) : (
                  topJobs.map(j => (
                    <tr key={j.job_id || j.jobId}>
                      <td>{j.job_id || j.jobId}</td>
                      <td>{j.name}</td>
                      <td>{j.postcode}</td>
                      <td>{j.created_at ? new Date(j.created_at).toLocaleString() : ''}</td>
                      <td>{j.jobStatus || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelTitle}>Interactions (Latest)</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={4}><div className={styles.skeleton} /></td></tr>
                  ))
                ) : topInteractions.length === 0 ? (
                  <tr><td colSpan={4}>No interaction data.</td></tr>
                ) : (
                  topInteractions.map((it, idx) => (
                    <tr key={it.id || idx}>
                      <td>{it.timestamp ? new Date(it.timestamp).toLocaleString() : ''}</td>
                      <td>{it.user || it.userName || it.businessName || ''}</td>
                      <td>{it.action || it.type || ''}</td>
                      <td>{it.message || it.description || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Catalog quick look */}
          <div className={styles.panelTitle} style={{ marginTop: 16 }}>Subscription Types (Catalog)</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Type Id</th>
                  <th>Title</th>
                  <th>My Type</th>
                  <th>Price</th>
                  <th>Stripe Product</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <tr key={i}><td colSpan={5}><div className={styles.skeleton} /></td></tr>
                  ))
                ) : subscriptionTypes.slice(0, 5).map((s) => (
                  <tr key={s.subscriptionTypeId}>
                    <td>{s.subscriptionTypeId}</td>
                    <td>{s.title}</td>
                    <td>{s.myType}</td>
                    <td>{typeof s.price !== 'undefined' ? `$${s.price}` : ''}</td>
                    <td>{s.stripeProductId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.panelTitle} style={{ marginTop: 16 }}>Quote Templates (Sample)</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Template Id</th>
                  <th>Business User</th>
                  <th>Created</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <tr key={i}><td colSpan={4}><div className={styles.skeleton} /></td></tr>
                  ))
                ) : quoteTemplates.slice(0, 5).map((qt) => (
                  <tr key={qt.id}>
                    <td>{qt.id}</td>
                    <td>{qt.businessUserId}</td>
                    <td>{qt.createdAt ? new Date(qt.createdAt).toLocaleString() : ''}</td>
                    <td>{qt.templateMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
